// v0.5.0 — Preset reason templates for the 5 audit-text fields
// signed off operator round C (2026-06-20). Compliance-adjacent
// wording — DO NOT edit the templates without explicit Y/N
// sign-off; what lands here ends up embedded in filed XLSX
// audit trails.
//
// Token convention: `<TOKEN_NAME>` placeholders are
// operator-fillable. The ReasonPicker enforces (placeholder
// validation `a` — fail loud) that no `<...>` tokens remain in
// the saved text. The regex used by ReasonPicker is
// `/<[A-Z_][A-Z0-9_]*>/g`.

// A2-571 (operator 2026-10-27): no "Other (explain)" free-text option — every
// reason is picked from the list.

export const TYPED_IN_EDITOR_PRESETS = [
  {
    id: "no-sales",
    label: "No sales at these rates this period",
    // v0.5.0c+3 — operator round-D sign-off (2026-06-20): the
    // `<RATE_DESC>` token was removed from this preset. Rationale:
    // "no sales recorded at <rate>" is a logical contradiction —
    // you can't meaningfully specify which rate there were no
    // sales at when the whole sentence asserts there were none.
    // The box title already scopes which rate bucket, so no
    // audit information is lost. Coordinated with TypedInEditor's
    // value-auto-fill (picking this preset auto-fills value=0
    // when the value field is currently empty).
    template: "No sales recorded for this reporting period.",
  },
  {
    id: "manual-calc",
    label: "Sales exist — manually calculated",
    // v0.5.0c+4 — operator round-D extended (2026-06-20). The
    // `<RATE_DESC>` token was removed for the same reason as the
    // `no-sales` and `not-applicable` presets: the box title
    // already declares which rate bucket this is about, AND on
    // multi-rate boxes (Box 35 = legacy 16% / 5%) the rate-filler
    // dropdown would have offered the active rates (19% / 7% /
    // 0%), letting an operator save the wrong rate into an audit
    // trail. The `<DOC_REF>` token stays — the workpaper
    // reference is a substantive audit fact independent of the
    // box label.
    template:
      "Sales in this category exist. Value above is manually "
      + "calculated from source records; workpaper retained at <DOC_REF>.",
  },
  {
    id: "not-applicable",
    label: "Not applicable to this business",
    // v0.5.0c+4 — operator round-D extended (2026-06-20). The
    // `<RATE_DESC>` token was removed for the same reason as the
    // `no-sales` and `manual-calc` presets. "Not applicable to
    // this business" is a blanket statement about the operator's
    // product mix, not a per-rate claim; the box title carries
    // which bucket. `<ROLE>` and `<DATE>` stay — who declared,
    // when, are substantive audit facts.
    template:
      "Entity's product/service mix is not subject to this category of "
      + "sales. Confirmed by <ROLE> on <DATE>.",
  },
  {
    id: "from-accountant",
    label: "Sourced from accountant",
    template:
      "Value above provided by accountant <ACCOUNTANT_NAME> on <DATE>; "
      + "engine output cross-checked and consistent.",
  },
];

export const SIGNOFF_PRESETS = [
  {
    id: "standard",
    label: "Standard sign-off",
    template:
      "Calc reviewed against source records on <DATE>. All known "
      + "issues resolved. Filed under my professional responsibility.",
  },
  {
    id: "with-accountant",
    label: "Reviewed jointly with accountant",
    template:
      "Reviewed jointly with accountant <NAME> on <DATE>. "
      + "Approved for filing.",
  },
  {
    id: "material-findings",
    label: "Material findings — resolved",
    template:
      "Material findings during review: <DESC>. Each resolved at "
      + "source before sign-off.",
  },
];

const REVIEW_GENERIC = [
  {
    id: "reviewed-at-source",
    label: "Reviewed at source",
    template:
      "Reviewed against source records on <DATE>; no correction required.",
  },
  {
    id: "sampled",
    label: "Bulk-ack after sampling",
    template:
      "Sampled <SAMPLE_COUNT> of <ROW_COUNT> affected rows; no "
      + "material issues; bulk-ack.",
  },
];

const REVIEW_MISSING_FX = [
  {
    id: "ecb-backfill",
    label: "FX back-filled from ECB",
    template:
      "Exchange rates back-filled from ECB euro reference rate for "
      + "<DATE_RANGE>.",
  },
  {
    id: "marketplace-rate",
    label: "Marketplace rate accepted",
    template:
      "Original marketplace-provided FX rate accepted as authoritative "
      + "for <ROW_COUNT> rows.",
  },
  {
    id: "below-materiality",
    label: "Below materiality",
    template:
      "Affected rows total <AMOUNT>, below materiality threshold; "
      + "acknowledged.",
  },
];

const REVIEW_AMOUNT_MISMATCH = [
  {
    id: "within-tolerance",
    label: "Within engine tolerance",
    template:
      "Mismatch within engine tolerance band, attributable to "
      + "marketplace rounding; rate consistency confirmed.",
  },
  {
    id: "manual-recalc",
    label: "Manual recalc matches",
    template:
      "Manual recalculation on <ROW_COUNT> sample rows matches engine "
      + "totals; bulk-ack.",
  },
];

export const REVIEW_PRESETS_BY_FLAG_TYPE = {
  MISSING_EXCHANGE_RATE: REVIEW_MISSING_FX,
  AMOUNT_MISMATCH_TOLERANCE_5PCT: REVIEW_AMOUNT_MISMATCH,
};

export const reviewPresetsFor = (flagType) =>
  REVIEW_PRESETS_BY_FLAG_TYPE[flagType] || REVIEW_GENERIC;

export const VIES_PRESETS = [
  {
    id: "direct-confirm",
    label: "Confirmed via direct customer contact",
    template:
      "VAT number confirmed valid by direct customer contact on <DATE>.",
  },
  {
    id: "reg-cert-on-file",
    label: "Registration cert on file",
    template:
      "Customer registration certificate on file at <DOC_REF>; "
      + "treating as valid B2B.",
  },
  {
    id: "vies-outage",
    label: "Known VIES outage",
    template:
      "VIES returned unknown for <COUNTRY> — confirmed temporary "
      + "VIES outage on <DATE>. Released.",
  },
];

// v0.5.0c — Sign-off C round, Supersede + Re-upload paths from
// the Inspect drawer. These wordings ARE compliance-adjacent —
// embedded in the new calc's signoff `notes` field. Do not edit
// without explicit Y/N sign-off.
export const SUPERSEDE_PRESETS = [
  {
    id: "marketplace-rate-corrected",
    label: "Source rate corrected at marketplace",
    template:
      "Marketplace updated <RATE>% rows on <DATE>; re-ingest queued; "
      + "this calc superseded.",
  },
  {
    id: "operator-wrong-rate",
    label: "Operator entered wrong rate at source",
    template:
      "Internal source-data fix on <ROW_COUNT> rows; this calc "
      + "superseded with corrected ingest.",
  },
  {
    id: "bad-csv-mapping",
    label: "Bad CSV column mapping",
    template:
      "CSV column mapping correction applied for adapter <ADAPTER>; "
      + "this calc superseded.",
  },
];

export const OFFENDER_NOTE_PRESETS = [
  {
    id: "tax-authority-published",
    label: "New rate published by tax authority",
    template:
      "<COUNTRY> tax authority published <RATE>% effective <DATE>; "
      + "source: <URL>.",
  },
  {
    id: "marketplace-band",
    label: "Marketplace introduced new rate band",
    template:
      "<MARKETPLACE> began applying <RATE>% on <DATE>; pattern "
      + "observed across <ROW_COUNT> rows.",
  },
  {
    id: "source-data-error",
    label: "Suspected source-data error",
    template:
      "Suspected source-data error. Rate <RATE>% appears on "
      + "<ROW_COUNT> rows; investigating at source.",
  },
];

// v0.5.0d — Manual-override routing presets. Operator picks one
// to populate the audit reason on the new
// `POST /api/runs/calc/{calc_id}/txn-override` route. Embedded
// in the new calc's `country_return_overrides` row + the per-txn
// audit row (vat_transaction_overrides). DO NOT edit without
// explicit Y/N sign-off.
export const MANUAL_ROUTE_PRESETS = [
  {
    id: "legacy-rate-routed",
    label: "Legacy rate routed to manual box",
    template:
      "Legacy/transitional VAT rate <RATE>% on <ROW_COUNT> rows; "
      + "routed to the form's manual-other-rates box pending "
      + "permanent rate-table patch.",
  },
  {
    id: "new-jurisdictional-rate",
    label: "New jurisdictional rate — file today, table-fix follows",
    template:
      "<COUNTRY> introduced <RATE>% effective <DATE>; routing this "
      + "period's <ROW_COUNT> rows to manual box so the filing can "
      + "proceed; rate-table patch tracked separately.",
  },
  {
    id: "marketplace-band-route",
    label: "Marketplace band — route to other-rates",
    template:
      "<MARKETPLACE> applied <RATE>% across <ROW_COUNT> rows in "
      + "this period; routing to manual other-rates box for this "
      + "filing.",
  },
];

// Single regex applied by ReasonPicker.isValid against trimmed
// text. Matches any UPPER_SNAKE_CASE token wrapped in `< >`.
export const PLACEHOLDER_RE = /<[A-Z_][A-Z0-9_]*>/g;

export const remainingPlaceholders = (text) =>
  Array.from((text || "").matchAll(PLACEHOLDER_RE)).map((m) => m[0]);

// v0.6.12 (A1-61) — plain-English name for each token, so the "you
// still have something to fill in" message can say WHAT is wanted
// instead of showing the token.
//
// The operator reported the old message as "some error, like replace
// placeholder token doc underscore ref" — which is exactly how it read:
// ReasonPicker rendered the literal `<DOC_REF>` and the words
// "placeholder token" to a non-technical operator, breaking the
// standing no-engine-vocabulary-on-screen rule. The line even carried
// an i18n-ignore, so it was not translated either.
//
// These are UI labels, not audit text: they never enter a saved reason,
// so they are outside this file's "DO NOT edit the templates without
// Y/N sign-off" rule. Every token appearing in any preset above has an
// entry, pinned by a test — an unmapped token would silently fall back
// to showing itself, which is the defect this fixes.
export const TOKEN_LABELS = {
  "<ACCOUNTANT_NAME>": "the accountant's name",
  "<ADAPTER>": "which upload format",
  "<AMOUNT>": "the amount",
  "<COUNTRY>": "the country",
  "<DATE>": "the date",
  "<DATE_RANGE>": "the dates covered",
  "<DESC>": "a short description",
  "<DOC_REF>": "where the workpaper is kept",
  "<MARKETPLACE>": "which marketplace",
  "<NAME>": "the name",
  "<RATE>": "the VAT rate",
  "<RATE_DESC>": "which rate band",
  "<ROLE>": "who confirmed it",
  "<ROW_COUNT>": "how many rows",
  "<SAMPLE_COUNT>": "how many you checked",
  "<URL>": "the web address",
};

export const describePlaceholders = (tokens) =>
  (tokens || []).map((t) => TOKEN_LABELS[t] || t);
